import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { listSearchSchema } from '@/shared/patterns/list-search';

import { operatorListFilters } from './bank-account-tabs.filters';
import { beneficiaryListFilters } from './beneficiary.filters';
import { bankAccountListFilters } from './bank-account.filters';
import { paymentListFilters } from './payment.filters';
import { pixListFilters } from './pix.filters';
import { transferListFilters } from './transfer.filters';

// Leve de propósito: entra no arquivo da rota, que vai para o bundle inicial (A01). Só zod, Lingui,
// filtros e `shared/patterns/list-search` direto (nunca o barril).

/** Caminho do menu (`navigation.ts:57`, `bankAccountRoute`): é por ele que `screens.byPath` acha a tela. */
export const BANK_ACCOUNT_LIST_PATH = '/contas-digitais/contas';
export const bankAccountPath = (id: string) => `${BANK_ACCOUNT_LIST_PATH}/${encodeURIComponent(id)}`;
export const transitoryAccountPath = (id: string) => `${bankAccountPath(id)}/visualizar`;

/**
 * As abas do legado (ícones do mapa da casca, `app/shell/icons.tsx`), na ordem da `TabBar` com Pagamentos já no lugar (`BankAccountViewForm.tsx:62-81`, U44).
 * `segment` é o caminho da aba no legado; as que não migraram mostram o estado Em construção.
 */
export const BANK_ACCOUNT_TABS = [
  { id: 'extrato', label: msg`Extrato`, icon: 'receipt-text', segment: '' },
  { id: 'transferencias', label: msg`Transferências`, icon: 'wallet', segment: 'transferencias' },
  { id: 'pix', label: msg`Pix`, icon: 'wallet', segment: 'pix' },
  { id: 'pagamentos', label: msg`Pagamentos`, icon: 'file-text', segment: 'pagamentos' },
  { id: 'operadores', label: msg`Operadores`, icon: 'user', segment: 'operadores' },
  { id: 'acesso-ao-aplicativo', label: msg`Acesso ao aplicativo`, icon: 'monitor', segment: 'acesso-ao-aplicativo' },
  { id: 'favorecidos', label: msg`Favorecidos`, icon: 'handshake', segment: 'favorecidos' },
  { id: 'meus-limites', label: msg`Meus limites`, icon: 'shield-check', segment: 'meus-limites' },
  { id: 'credores', label: msg`Credores`, icon: 'building-2', segment: 'credores' },
] as const;
export type BankAccountTab = (typeof BANK_ACCOUNT_TABS)[number]['id'];
export const BANK_ACCOUNT_TAB_IDS = BANK_ACCOUNT_TABS.map((tab) => tab.id) as [BankAccountTab, ...BankAccountTab[]];

/** Na conta transitória só estas quatro (`BankAccountViewForm.tsx:92-95`, `router.tsx:498-518`). */
export const TRANSITORY_TAB_IDS = ['extrato', 'transferencias', 'pix', 'pagamentos'] as const satisfies readonly BankAccountTab[];
export const TRANSITORY_TABS = BANK_ACCOUNT_TABS.filter((tab) => (TRANSITORY_TAB_IDS as readonly string[]).includes(tab.id));

/** Caminhos antigos das abas (`router.tsx:411-482`): hoje redirecionam para `?tab=`. */
export const LEGACY_TAB_SEGMENTS = BANK_ACCOUNT_TABS.filter((tab) => tab.segment !== '').map((tab) => tab.segment);

export const bankAccountListSearch = listSearchSchema({ ...bankAccountListFilters.shape });
export type BankAccountListSearch = z.infer<typeof bankAccountListSearch>;

/** Ids dos períodos do extrato, na ordem dos botões (`StatementFilters.tsx:9-17`); `hoje` não vai para a URL. */
export const STATEMENT_PERIOD_IDS = ['hoje', '7', '15', '30', '60', '90', 'definir'] as const;
export type StatementPeriod = (typeof STATEMENT_PERIOD_IDS)[number];

/** Tamanhos da paginação no cliente (`AccountStatementTabList.tsx:145`). */
export const STATEMENT_PAGE_SIZES = [5, 10, 20, 50] as const;

const civil = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Painel "Exportar QSB" pela URL (U26): o voltar do navegador fecha. */
export const QSB_SHEET = 'exportar-qsb';

/**
 * URL do detalhe: aba (a primeira não vai para a URL), período do extrato (`periodo`, com `inicio`/`fim`
 * no `definir`), tipo de data (`data=contabil`), página e tamanho (do extrato ou da aba aberta, que são
 * excludentes), o painel do QSB e os filtros das abas Operadores, Favorecidos, Transferências, Pix e Pagamentos. Os
 * de Favorecidos (`fav_*`), Transferências (`ted_*`), Pix (`pix_*`) e Pagamentos (`pay_*`) têm chave própria: os fios
 * usam `searchString` e `f_status`.
 */
export const bankAccountDetailSearch = listSearchSchema(
  {
    tab: z.enum(BANK_ACCOUNT_TAB_IDS).optional().catch(undefined),
    periodo: z.enum(STATEMENT_PERIOD_IDS).optional().catch(undefined),
    inicio: civil.optional().catch(undefined),
    fim: civil.optional().catch(undefined),
    data: z.enum(['contabil']).optional().catch(undefined),
    sheet: z.enum([QSB_SHEET]).optional().catch(undefined),
    ...operatorListFilters.shape,
    ...beneficiaryListFilters.shape,
    ...transferListFilters.shape,
    ...pixListFilters.shape,
    ...paymentListFilters.shape,
  },
  { sizes: STATEMENT_PAGE_SIZES },
);
export type BankAccountDetailSearch = z.infer<typeof bankAccountDetailSearch>;
